import type { LineString, MultiLineString, MultiPolygon, Point as PointGeometry, Polygon, Position } from "geojson";
import { Feature } from "geojson";

import {
  distance,
  featureCollection,
  getCoords,
  lineString as turfLineString,
  midpoint,
  nearestPoint,
  nearestPointOnLine,
  point as turfPoint,
  polygonToLine,
} from "fond/turf";

/**
 * Custom version of calcClosestLayer that prioritizes Point features over LineString/Polygon features
 * when distances are very similar.
 *
 * This ensures that when a Point is colocated with a LineString, snapping will prefer the Point.
 *
 * @param lngLat - The position to snap from
 * @param layers - The features to consider for snapping
 * @param pointPriorityDistance - Maximum distance difference (in km) to prefer Point over Line (default: 0.001 km = 1 meter)
 */
function calcClosestLayerWithPointPriority(lngLat: { lng: number; lat: number }, layers: Feature[], pointPriorityDistance: number = 0.001): any {
  const calcLayerDistances = (lngLatParam: { lng: number; lat: number }, layer: Feature) => {
    const P = [lngLatParam.lng, lngLatParam.lat];
    const isMarker = layer.geometry.type === "Point";
    const isPolygon = layer.geometry.type === "Polygon";
    const isMultiPolygon = layer.geometry.type === "MultiPolygon";
    const isMultiPoint = layer.geometry.type === "MultiPoint";

    const latlngs = getCoords(layer as Feature<PointGeometry | MultiPolygon | Polygon | LineString | MultiLineString>);

    if (isMarker) {
      const [lng, lat] = latlngs as [number, number];
      return {
        latlng: { lng, lat },
        distance: distance(latlngs as Position, P as Position),
        isMarker: true,
      };
    }

    if (isMultiPoint) {
      const np = nearestPoint(P, featureCollection((latlngs as number[][]).map((x: number[]) => turfPoint(x))));
      const c = np.geometry.coordinates;
      return {
        latlng: { lng: c[0], lat: c[1] },
        distance: np.properties.distanceToPoint,
        isMarker: true,
      };
    }

    let lines: Feature<LineString | MultiLineString> | ReturnType<typeof featureCollection> =
      isPolygon || isMultiPolygon ? polygonToLine(layer as Feature<Polygon | MultiPolygon>) : (layer as Feature<LineString | MultiLineString>);
    let nearestPt: Feature<PointGeometry>;

    if (isPolygon) {
      const lineStrings =
        lines.geometry.type === "LineString"
          ? [turfLineString(lines.geometry.coordinates)]
          : lines.geometry.coordinates.map((coords: number[][]) => turfLineString(coords));

      const nearestPointsOfEachFeature = lineStrings.map((feat: any) => ({
        feature: feat,
        point: nearestPointOnLine(feat, P),
      }));
      nearestPointsOfEachFeature.sort((a: any, b: any) => a.point.properties.dist - b.point.properties.dist);

      lines = nearestPointsOfEachFeature[0].feature;
      nearestPt = nearestPointsOfEachFeature[0].point;
    } else if (isMultiPolygon) {
      const lineStrings = lines.features
        .map((feat: any) => {
          if (feat.geometry.type === "LineString") {
            return [feat.geometry.coordinates];
          } else {
            return feat.geometry.coordinates;
          }
        })
        .flatMap((coords: number[][][]) => coords)
        .map((coords: number[][]) => turfLineString(coords));

      const nearestPointsOfEachFeature = lineStrings.map((feat: any) => ({
        feature: feat,
        point: nearestPointOnLine(feat, P),
      }));
      nearestPointsOfEachFeature.sort((a: any, b: any) => a.point.properties.dist - b.point.properties.dist);

      lines = nearestPointsOfEachFeature[0].feature;
      nearestPt = nearestPointsOfEachFeature[0].point;
    } else {
      nearestPt = nearestPointOnLine(lines as Feature<LineString | MultiLineString>, P);
    }

    const [lng, lat] = nearestPt.geometry.coordinates;
    let segmentIndex = nearestPt.properties?.index ?? 0;
    let { coordinates } = lines.geometry;

    if (lines.geometry.type === "MultiLineString") {
      coordinates = lines.geometry.coordinates[nearestPt.properties?.multiFeatureIndex ?? 0];
    }

    if (segmentIndex + 1 === coordinates.length) {
      segmentIndex -= 1;
    }

    return {
      latlng: { lng, lat },
      segment: coordinates.slice(segmentIndex, segmentIndex + 2),
      distance: nearestPt.properties?.dist ?? 0,
      isMarker: false,
    };
  };

  let closestLayer: any = {};
  let closestPoint: any = null;

  // First pass: find all candidates and separate points from lines/polygons
  layers.forEach((layer) => {
    const results = calcLayerDistances(lngLat, layer);

    if (results.isMarker) {
      // Track closest point separately
      if (!closestPoint || results.distance < closestPoint.distance) {
        closestPoint = { ...results, layer };
      }
    }

    // Track overall closest
    if (closestLayer.distance === undefined || results.distance < closestLayer.distance) {
      closestLayer = { ...results, layer };
    }
  });

  // If we have a point and it's within threshold of the closest layer, prefer the point
  if (closestPoint && closestLayer.distance !== undefined) {
    const distanceDiff = Math.abs(closestPoint.distance - closestLayer.distance);
    if (distanceDiff < pointPriorityDistance) {
      return closestPoint;
    }
  }

  return closestLayer;
}

/**
 * Custom snap function that prioritizes Point features over LineStrings/Polygons.
 * Wraps the original snap logic but uses our custom calcClosestLayerWithPointPriority.
 */
export function snapWithPointPriority(state: any, e: any): { lng: number; lat: number } {
  let { lng } = e.lngLat;
  let { lat } = e.lngLat;

  // Holding alt bypasses all snapping
  if (e.originalEvent.altKey) {
    state.showVerticalSnapLine = false;
    state.showHorizontalSnapLine = false;
    return { lng, lat };
  }

  if (!state.snapList || state.snapList.length <= 0) {
    return { lng, lat };
  }

  let closestLayer: any;
  let minDistance: number;
  let snapLatLng: { lng: number; lat: number } | undefined;

  if (state.options.snap) {
    // Get point priority distance from options (in km), default to 1 meter
    const pointPriorityDistance = state.options.snapOptions?.pointPriorityDistance ?? 0.001;

    // Use our custom function with point priority
    closestLayer = calcClosestLayerWithPointPriority({ lng, lat }, state.snapList, pointPriorityDistance);

    if (Object.keys(closestLayer).length === 0) {
      return { lng, lat };
    }

    const { isMarker } = closestLayer;
    const snapVertexPriorityDistance = state.options.snapOptions?.snapVertexPriorityDistance ?? 1.25;

    if (isMarker) {
      // Point feature - snap directly to it
      snapLatLng = closestLayer.latlng;
    } else {
      // Line/Polygon feature - check if we should snap to vertex or edge
      const A = closestLayer.segment[0];
      const B = closestLayer.segment[1];
      const C = [closestLayer.latlng.lng, closestLayer.latlng.lat];

      const distanceAC = distance(A, C);
      const distanceBC = distance(B, C);

      let closestVertexLatLng = distanceAC < distanceBC ? A : B;
      let shortestDistance = distanceAC < distanceBC ? distanceAC : distanceBC;

      // Check midpoint if enabled
      if (state.options.snapOptions?.snapToMidPoints) {
        const M = midpoint(A, B).geometry.coordinates;
        const distanceMC = distance(M, C);

        if (distanceMC < distanceAC && distanceMC < distanceBC) {
          closestVertexLatLng = M;
          shortestDistance = distanceMC;
        }
      }

      // Snap to vertex if close enough, otherwise snap to edge
      if (shortestDistance < snapVertexPriorityDistance) {
        const [lngSnap, latSnap] = closestVertexLatLng;
        snapLatLng = { lng: lngSnap, lat: latSnap };
      } else {
        snapLatLng = { lng: C[0], lat: C[1] };
      }
    }

    // Calculate snap distance threshold in meters
    const metersPerPixel = (latitude: number, zoomLevel: number) => {
      const earthCircumference = 40075017;
      const latitudeRadians = latitude * (Math.PI / 180);
      return (earthCircumference * Math.cos(latitudeRadians)) / 2 ** (zoomLevel + 8);
    };

    const snapPx = state.options.snapOptions?.snapPx ?? 15;
    minDistance = snapPx * metersPerPixel(snapLatLng!.lat, state.map.getZoom());
  }

  // Check if within snap distance
  if (closestLayer && snapLatLng && closestLayer.distance * 1000 < minDistance!) {
    return snapLatLng;
  }

  return { lng, lat };
}
